import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { captureException } from '@sentry/react';
import { useMutation, useQuery } from '@apollo/client';
import { SPLIT_ORDER, SET_SHIPPER_BILLABLE } from './graphql/mutations';
import { SPLIT_ORDER_READ, SPLIT_BUTTON_READ } from './graphql/queries';
import { BOARD_MODES, BOARD_MODE_LABELS, FALLBACK_BLOCKED, REFRESH_ERROR } from './constants';
import { useFocusAfterUpdate } from './useFocusAfterUpdate';
import {
    addOrder,
    assignClaim,
    assignService,
    blockerReasons,
    buildSplitVariables,
    canDivideLine,
    divideLine,
    initialLayout,
    layoutValidity,
    lineCountReason,
    mergeRefreshedLayout,
    modeBadges,
    moveLine,
    moveTargets,
    problemMode,
    readSplitError,
    readSplitResult,
    removeOrder,
    setOrderBillable,
    splitButtonState,
} from './utils';

/**
 * Submit a split. Sends one `split_order` call at a time: a second call while one is in flight is ignored.
 *
 * @param {{ onSplit?: (result: { parent: object|null, children: object[] }) => void }} [options]
 *   `onSplit` runs after a successful split so the caller can refetch the order.
 * @returns {{ submitSplit: (args: { orderId: string, orderStatus: string, items: object[], layout: import('./utils').SplitLayout, services?: object[], claims?: object[] }) => Promise<{ parent: object|null, children: object[] }|{ error: Error }|null>, submitting: boolean }}
 *   `submitSplit` takes the split board's `layout` (with the parent's `items`, `services` and `claims`) and builds the call
 *   with `buildSplitVariables`. It resolves to `{ parent, children }` on success,
 *   `{ error }` on failure, or null when ignored.
 */
export function useSplitOrder({ onSplit } = {}) {
    const [splitOrder] = useMutation(SPLIT_ORDER);
    const inFlight = useRef(false);
    const [submitting, setSubmitting] = useState(false);

    const submitSplit = useCallback(
        async ({ orderId, orderStatus, items, layout, services, claims }) => {
            if (inFlight.current) return null;
            inFlight.current = true;
            setSubmitting(true);
            let outcome;
            try {
                const variables = buildSplitVariables({ orderId, orderStatus, items, layout, services, claims });
                const { data } = await splitOrder({ variables });
                outcome = readSplitResult(data?.split_order);
            } catch (error) {
                return { error };
            } finally {
                inFlight.current = false;
                setSubmitting(false);
            }
            // Outside the try so a throwing callback cannot turn a finished split into an error.
            try {
                onSplit?.(outcome);
            } catch (callbackError) {
                console.error(callbackError); // eslint-disable-line no-console
            }
            return outcome;
        },
        [splitOrder, onSplit]
    );

    return { submitSplit, submitting };
}

/**
 * Read the order fresh for the modal: items, status, blockers, and `last_modified_at`. The read is `network-only`
 * (it exists to get past what the page cached) and is skipped while the modal is closed or has no order id.
 * There is no automatic retry. `refresh` re-reads on demand, for example after a failed split.
 *
 * @param {{ open: boolean, orderId?: string }} args The modal's open flag and the order to read.
 * @returns {{ order: object|null, items: object[], blockers: string[], loading: boolean, error: Error|undefined, refresh: () => Promise<object[]|null>, refreshOrder: () => Promise<object|null> }}
 *   `refresh` resolves to the new item list, and `refreshOrder` to the whole re-read order (items, services, and claims);
 *   both resolve to null if the re-read failed.
 */
export function useSplitOrderRead({ open, orderId }) {
    // Do not set `notifyOnNetworkStatusChange`: with it, `loading` turns true during `refetch()`, and the board
    // would blank (and show the loading state) during the re-read after a failed split.
    const { data, loading, error, refetch } = useQuery(SPLIT_ORDER_READ, {
        variables: { order_id: orderId },
        fetchPolicy: 'network-only',
        skip: !open || !orderId,
    });
    // While a read is running, ignore `data`: on a reopen Apollo can hand back the previous result first.
    const order = !loading && data?.orders?.[0] ? data.orders[0] : null;

    const refreshOrder = useCallback(async () => {
        try {
            const result = await refetch();
            if (result?.error) {
                captureException(result.error);
                return null;
            }
            return result?.data?.orders?.[0] || null;
        } catch (refreshError) {
            captureException(refreshError);
            return null;
        }
    }, [refetch]);

    const refresh = useCallback(async () => (await refreshOrder())?.items || null, [refreshOrder]);

    return {
        order,
        items: order?.items || [],
        blockers: order?.split_blockers || [],
        loading,
        error,
        refresh,
        refreshOrder,
    };
}

/**
 * Read what the Split Order button needs: `split_blockers` and the item lines. Skipped when `skip` is true, so
 * an order that never shows the button costs nothing. Shows cached data first and refreshes from the server.
 *
 * @param {{ orderId?: string, skip?: boolean }} args The order and whether to skip the read.
 * @returns {{ loading: boolean, hidden: boolean, reasons: string[] }} `loading` is true until the first result, and the
 *   button is hidden until then. `hidden` hides the button. `reasons` has one line per reason, empty when enabled.
 */
export function useSplitButtonState({ orderId, skip = false } = {}) {
    const { data, error } = useQuery(SPLIT_BUTTON_READ, {
        variables: { order_id: orderId },
        fetchPolicy: 'cache-and-network',
        skip: skip || !orderId,
    });
    useEffect(() => {
        if (error) captureException(error);
    }, [error]);
    const order = data?.orders?.[0];
    if (!order) {
        // Hidden until the read answers, so a button a blocker would hide (NOT_ENABLED) never flashes on load.
        // A failed read leaves the button enabled (no reasons); the server still decides on submit.
        return error ? { loading: false, hidden: false, reasons: [] } : { loading: true, hidden: true, reasons: [] };
    }
    return {
        loading: false,
        ...splitButtonState({ blockers: order.split_blockers, items: order.items, orderStatus: order.order_status }),
    };
}

/**
 * Set a split child's `shipper_billable`. One call at a time: a second call while one is in flight is ignored.
 *
 * @returns {{ setBillable: (orderId: string, next: boolean) => Promise<{ order: object|null }|{ error: Error }|null>, saving: boolean }}
 *   `setBillable` resolves to `{ order }` (the updated order) on success, `{ error }` on failure, or null when ignored.
 */
export function useSetShipperBillable() {
    const [setShipperBillable] = useMutation(SET_SHIPPER_BILLABLE);
    const inFlight = useRef(false);
    const [saving, setSaving] = useState(false);
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return () => {
            mounted.current = false;
        };
    }, []);

    const setBillable = useCallback(
        async (orderId, next) => {
            if (inFlight.current) return null;
            inFlight.current = true;
            setSaving(true);
            try {
                const { data } = await setShipperBillable({
                    variables: { order_id: orderId, shipper_billable: next },
                });
                return { order: data?.update_orders_by_pk || null };
            } catch (error) {
                return { error };
            } finally {
                inFlight.current = false;
                if (mounted.current) setSaving(false);
            }
        },
        [setShipperBillable]
    );

    return { setBillable, saving };
}

/**
 * Submit the split board's layout and recover from a failure. Wraps `useSplitOrder`, so only one call runs at a
 * time. On failure it keeps the server's message (verbatim, or a generic one for a network error, which is also
 * logged), re-reads the order, and fits the layout to the new read with `mergeRefreshedLayout` through `setLayout`.
 * When the re-read also fails, `refreshFailed` is set and the layout is left as it was. `submitting` stays true
 * through the re-read, and a submit during it is ignored.
 *
 * @param {{ onSplit?: (result: { parent: object|null, children: object[] }) => void, refreshOrder: () => Promise<object|null>, setLayout: (update: (layout: import('./utils').SplitLayout) => import('./utils').SplitLayout) => void }} args
 *   `refreshOrder` comes from `useSplitOrderRead`. `setLayout` is the modal's layout state setter.
 * @returns {{ submit: (args: { orderId: string, orderStatus: string, items: object[], services: object[], claims?: object[], layout: import('./utils').SplitLayout }) => Promise<{ parent: object|null, children: object[] }|null>, submitting: boolean, splitError: string|null, refreshFailed: boolean, clearErrors: () => void }}
 *   `submit` resolves to `{ parent, children }` on success, or null on failure or when a call is already running.
 */
export function useLayoutSplit({ onSplit, refreshOrder, setLayout }) {
    const { submitSplit, submitting } = useSplitOrder({ onSplit });
    const [splitError, setSplitError] = useState(null);
    const [refreshFailed, setRefreshFailed] = useState(false);
    // Covers the re-read after a failure, so a second submit can't send the layout before it is merged.
    const recoveringRef = useRef(false);
    const [recovering, setRecovering] = useState(false);

    const clearErrors = useCallback(() => {
        setSplitError(null);
        setRefreshFailed(false);
    }, []);

    const submit = useCallback(
        async ({ orderId, orderStatus, items, services, claims, layout }) => {
            if (recoveringRef.current) return null;
            const outcome = await submitSplit({ orderId, orderStatus, items, services, claims, layout });
            if (!outcome) return null;
            if (!outcome.error) {
                clearErrors();
                return outcome;
            }
            const { message, isNetwork } = readSplitError(outcome.error);
            if (isNetwork) captureException(outcome.error);
            setSplitError(message);
            setRefreshFailed(false);
            recoveringRef.current = true;
            setRecovering(true);
            try {
                const fresh = await refreshOrder();
                if (fresh)
                    setLayout((current) => mergeRefreshedLayout(current, fresh.items, fresh.services, fresh.claims));
                else setRefreshFailed(true);
            } finally {
                recoveringRef.current = false;
                setRecovering(false);
            }
            return null;
        },
        [submitSplit, refreshOrder, setLayout, clearErrors]
    );

    return { submit, submitting: submitting || recovering, splitError, refreshFailed, clearErrors };
}

/** A board card's drag handle by its card key, or null. */
const findDragHandle = (portionKey) =>
    portionKey
        ? [...document.querySelectorAll('[data-rbd-drag-handle-draggable-id]')].find(
              (node) => node.getAttribute('data-rbd-drag-handle-draggable-id') === portionKey
          ) || null
        : null;

/**
 * Everything the one-screen Split Order modal shows and does: the order read, the board's layout and mode, whether
 * Split order can run, the alert lines, and the handlers. The layout starts from `initialLayout` once the order is
 * read and resets, with the mode, each time the modal opens. A failed split keeps the layout (fitted to a re-read);
 * a successful one calls `onSplit` and then `onClose`.
 *
 * @param {{ open: boolean, orderId?: string, onClose: () => void, onSplit?: (result: { parent: object|null, children: object[] }) => void }} props
 *   The modal's own props.
 * @returns {{ order: object|null, items: object[], services: object[], claims: object[], layout: import('./utils').SplitLayout|null, mode: 'items'|'services'|'claims', setMode: (mode: 'items'|'services'|'claims') => void, loading: boolean, readError: Error|undefined, alertLines: string[], submitting: boolean, canSplit: boolean, splitDisabled: boolean, problemText: string, notice: string, board: { onMove: Function, onAssignService: Function, onAssignClaim: Function, onAddOrder: Function, onRemoveOrder: Function, onBillableChange: Function }, pendingMove: { move: import('./utils').LineMove, quantity: number, fromKey: string|null, lineNumber: number, item: object|null, fromLabel: string, toLabel: string }|null, confirmMove: (quantity: number) => void, cancelMove: () => void, handleSplit: () => Promise<void>, handleClose: () => void }}
 *   `loading` is true until the first read answers. `canSplit` is the layout rule (`layoutValidity`). `splitDisabled`
 *   is true when clicking Split order can do nothing at all (loading, blocked, or submitting); while it is false and
 *   `canSplit` is false, a click switches to the mode with a problem. `problemText` says what is left (empty when
 *   nothing is), and `notice` is the message to announce after such a click. `board` holds the board's change
 *   handlers. A new read of the order is merged into an edited layout with `mergeRefreshedLayout`. Moving a card
 *   that can be divided (`canDivideLine`) to another place sets `pendingMove` for the "How many to move?" prompt
 *   instead of moving it; `confirmMove` applies `divideLine` with the chosen quantity and `cancelMove` drops it.
 */
export function useSplitModal({ open, orderId, onClose, onSplit }) {
    const { order, items, blockers, loading, error: readError, refreshOrder } = useSplitOrderRead({ open, orderId });
    const services = order?.services || [];
    const claims = order?.claims || [];
    // Null until the user changes something; until then the board shows the starting layout for the read.
    const [layoutState, setLayoutState] = useState(null);
    const [mode, setMode] = useState(BOARD_MODES.ITEMS);
    // Memoised so the board sees one layout object per read, not a new one each render.
    const startLayout = useMemo(
        () => (order ? initialLayout(order.items, order.services, order.claims) : null),
        [order]
    );
    const layout = layoutState ?? startLayout;

    const updateLayout = useCallback(
        (update) => setLayoutState((current) => update(current ?? startLayout ?? initialLayout([], [], []))),
        [startLayout]
    );
    const { submit, submitting, splitError, refreshFailed, clearErrors } = useLayoutSplit({
        onSplit,
        refreshOrder,
        setLayout: updateLayout,
    });

    // Said in a polite live region after a click on an incomplete Split order switches the mode.
    const [notice, setNotice] = useState('');
    useEffect(() => {
        // Each open starts from a fresh layout in items mode.
        if (open) {
            setLayoutState(null);
            setMode(BOARD_MODES.ITEMS);
            setNotice('');
            clearErrors();
        }
    }, [open, clearErrors]);
    useEffect(() => {
        // A new read of the order (a refetch, or a cache update) fits the user's layout to it; untouched layouts
        // already follow the read through `startLayout`.
        if (order)
            setLayoutState((current) =>
                current ? mergeRefreshedLayout(current, order.items, order.services, order.claims) : current
            );
    }, [order]);
    useEffect(() => {
        if (readError) captureException(readError);
    }, [readError]);

    const orderStatus = order?.order_status;
    const lineReason = order ? lineCountReason(items) : null;
    const reasons = [...blockerReasons(blockers, orderStatus), ...(lineReason ? [lineReason] : [])];
    // A blocker code with no reason line of its own (ALREADY_SPLIT, unknown) still needs an explanation.
    const blockerLines = reasons.length === 0 && blockers.length > 0 ? [FALLBACK_BLOCKED] : reasons;
    const alertLines = [
        ...(splitError ? [splitError] : []),
        ...blockerLines,
        ...(refreshFailed ? [REFRESH_ERROR] : []),
    ];

    const validity = layout ? layoutValidity({ layout, items, services, claims, blockers }) : null;
    const canSplit = !!validity?.valid;
    const splitDisabled =
        !open || !layout || submitting || !!validity?.blocked || (!canSplit && !problemMode(validity));

    // What is left to do, for screen readers on the incomplete Split order button.
    const badges = layout ? modeBadges(layout, items, services, claims) : null;
    const problemText =
        !canSplit && !splitDisabled && badges
            ? [badges.items, ...(services.length ? [badges.services] : []), ...(claims.length ? [badges.claims] : [])]
                  .filter((badge) => badge.warn)
                  .map((badge) => badge.tooltip)
                  .join('. ')
            : '';

    const handleSplit = async () => {
        if (splitDisabled) return;
        if (!canSplit) {
            const target = problemMode(validity);
            setMode(target);
            setNotice(`Showing ${BOARD_MODE_LABELS[target]}: ${badges?.[target]?.tooltip || ''}`);
            return;
        }
        setNotice('');
        const result = await submit({ orderId, orderStatus, items, services, claims, layout });
        if (result) onClose();
    };

    const handleClose = () => {
        if (!submitting) onClose();
    };

    // A card move waiting on "How many to move?", with what the prompt shows.
    const [pendingMove, setPendingMove] = useState(null);
    // Focus after the prompt closes: the moved card after Move, the card's Move to button after Cancel.
    const focusAfterPrompt = useFocusAfterUpdate(pendingMove);
    useEffect(() => {
        if (open) setPendingMove(null);
    }, [open]);
    const itemOf = (itemId) => items.find((item) => item.item_id === itemId) || null;
    useEffect(() => {
        // The card changed or went away under the prompt (a re-read, for example): drop the prompt.
        if (!pendingMove) return;
        const line = layout?.lines.find((l) => l.portionKey === pendingMove.move.portionKey);
        if (!line || line.quantity !== pendingMove.quantity || (line.orderKey ?? null) !== pendingMove.fromKey) {
            setPendingMove(null);
        }
    }, [layout, pendingMove]);

    /** Apply a board move now and return true, or open the prompt for a card that can be divided and return false. */
    const requestMove = (move) => {
        const line = layout?.lines.find((l) => l.portionKey === move.portionKey);
        const target = move.toOrderKey ?? null;
        if (!line || !canDivideLine(line, itemOf(line.item_id)) || (line.orderKey ?? null) === target) {
            updateLayout((current) => moveLine(current, move));
            return true;
        }
        const labels = new Map(moveTargets(layout, order?.order_number).map((t) => [t.key, t.label]));
        const index = items.findIndex((item) => item.item_id === line.item_id);
        setPendingMove({
            move,
            quantity: line.quantity,
            fromKey: line.orderKey ?? null,
            lineNumber: index + 1,
            item: items[index] || null,
            fromLabel: labels.get(line.orderKey ?? null),
            toLabel: labels.get(target),
        });
        return false;
    };

    const confirmMove = (quantity) => {
        if (!pendingMove || submitting || !layout) return;
        const { move, item } = pendingMove;
        const next = divideLine(layout, { ...move, quantity }, item);
        const target = move.toOrderKey ?? null;
        const placed = next.lines.find((l) => l.item_id === item?.item_id && (l.orderKey ?? null) === target);
        focusAfterPrompt(() => findDragHandle(placed?.portionKey));
        updateLayout((current) => divideLine(current, { ...move, quantity }, item));
        setPendingMove(null);
    };

    const cancelMove = () => {
        const portionKey = pendingMove?.move.portionKey;
        focusAfterPrompt(() => findDragHandle(portionKey)?.querySelector('button[aria-haspopup]'));
        setPendingMove(null);
    };

    const board = {
        onMove: requestMove,
        onAssignService: (serviceId, orderKey) =>
            updateLayout((current) => assignService(current, serviceId, orderKey)),
        onAssignClaim: (claimId, orderKey) => updateLayout((current) => assignClaim(current, claimId, orderKey)),
        onAddOrder: () => updateLayout(addOrder),
        onRemoveOrder: (orderKey) => updateLayout((current) => removeOrder(current, orderKey)),
        onBillableChange: (orderKey, billable) =>
            updateLayout((current) => setOrderBillable(current, orderKey, billable)),
    };

    return {
        order,
        items,
        services,
        claims,
        layout,
        mode,
        setMode,
        loading: !!open && loading && !order,
        readError,
        alertLines,
        submitting,
        canSplit,
        splitDisabled,
        problemText,
        notice,
        board,
        pendingMove,
        confirmMove,
        cancelMove,
        handleSplit,
        handleClose,
    };
}
